import { memo, useCallback, useContext, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import AppGlobalsContext from "app/AppGlobalsContext";
import Button, { BUTTON_ICON_ALIGNMENTS, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";
import Map from "app/pages/.shared/Map/Map";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import Drawer from "app/pages/.shared/Drawer/Drawer";
import useVisualViewport from "app/utils/hooks/useVisualViewport";
import IconClose from "app/pages/.shared/IconClose";
import IconArrowRightSpacing from "app/pages/.shared/static/icons/IconArrowRightSpacing";
import useBlockScroll from "app/utils/hooks/useBlockScroll";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

import "./LocationModal.scss";

const LocationModal = ({ lat, lon, labelCta, title }) => {
	const { track } = useAnalytics();
	const [modalOpened, setModalOpen] = useState(false);
	const { resolution } = useContext(AppGlobalsContext);
	const [viewports] = useVisualViewport();
	const isMobile = resolution === RESOLUTION.SMALL;
	useBlockScroll(modalOpened);

	const handleOpenModal = useCallback(() => {
		setModalOpen(true);
		track("product-view-on-map-click", {
			productName: title,
		});
	}, []);

	const handleCloseModal = useCallback(() => {
		setModalOpen(false);
	}, []);

	const map = (
		<Map
			latitude={lat}
			longitude={lon}
			zoom={11}
			fullscreenControl={false}
			mapTypeControl={false}
			autoOpen={true}
		/>
	);

	return (
		<>
			{labelCta && (
				<Button
					variant={BUTTON_VARIANTS.BORDERLESS}
					onClick={handleOpenModal}
					loading={false}
					className="location-modal__cta"
					data-testid="location-modal-cta"
					icon={<IconArrowRightSpacing width={16} height={16} />}
					alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
				>
					{labelCta}
				</Button>
			)}
			{isMobile ? (
				<Drawer
					isOpen={modalOpened}
					direction={"rtl"}
					sidePanelClassName="location-side-panel"
					drawerClassName="location-side-panel__content"
					style={{ height: viewports?.visualViewport?.height }}
					blockScroll={true}
				>
					<div className="location-side-panel__header">
						<Typography
							variant={TYPOGRAPHY_VARIANTS.XL}
							isBold
							className="location-side-panel__header-title"
						>
							{title}
						</Typography>
						<div className="location-side-panel__header-icon">
							<IconClose width={24} height={24} onClick={handleCloseModal} />
						</div>
					</div>
					{map}
				</Drawer>
			) : (
				<Modal
					isOpen={modalOpened}
					onRequestClose={handleCloseModal}
					className="location-modal"
					overlayClassName="location-modal__overlay"
				>
					<div className="location-modal__content" data-testid="location-modal">
						<div className="location-modal__header">
							<Typography
								variant={TYPOGRAPHY_VARIANTS.XL}
								isBold
								className="location-side-panel__header-title"
							>
								{title}
							</Typography>
							<IconClose
								className="location-modal__close"
								data-testid="location-modal-close"
								onClick={handleCloseModal}
								width={24}
								height={24}
							/>
						</div>
						{map}
					</div>
				</Modal>
			)}
		</>
	);
};

LocationModal.propTypes = {
	lat: PropTypes.number,
	lon: PropTypes.number,
	labelCta: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

export default memo(LocationModal);
